import { defineStore } from 'pinia'
import { STORE_ID } from '~/constants/store'
import type { RelishRequest } from '~/composables/relish'
import { getApiCircuitBreakerState } from '~/composables/api'
import useAuthStore from '~/store/auth'
import useMenuStore from '~/store/menu'
import useOrderHistoryStore from '~/store/orderHistory'
import usePromotionsStore from '~/store/promotions'
import useNavigationMenuStore from '~/store/navigationMenu'
import useRelishStore from '~/store/relish'
import useRestaurantStore from '~/store/restaurant'
import type { PollingState as State } from '~/types/polling'

const CALLBACK_INTERVAL = 15000 // 15 seconds
const POLLING_INTERVAL = 60000 // 1 minute
const ORDER_STATUS_INTERVAL = 5000 // 5 seconds (will run every time the interval callback is called)
const RELISH_POLLING_INTERVAL = 600000 // 10 minutes
const RELISH_FAILED_POLLING_INTERVAL = 60000 // 1 minute
const initialState = (): State => ({
  intervalId: undefined
})

/**
 * @name usePollingStore
 * @description Store for polling data from the server.
 *
 */
const usePollingStore = defineStore(STORE_ID.POLLING, {
  state: initialState,

  getters: {},

  actions: {
    /**
     * @description Global polling check.
     * It includes:
     * - restaurant closing time to set restaurant availability; if restaurant is closed
     * an active cart will be expired
     * - order type and time to reset if restaurant is closed and order type is not Order at Table
     * - promotions to fetch if not fetched or older than 10 minutes
     * - relish product recommendations to fetch if not fetched or older than 1 hour
     * - relish coupons to fetch if not fetched or older than 5 minutes
     * - relish eightAndDonate to fetch if not fetched or older than 1 hour
     */
    async initPolling(callImmediately = false) {
      const authStore = useAuthStore()
      const menuStore = useMenuStore()
      const orderHistoryStore = useOrderHistoryStore()
      const promotionsStore = usePromotionsStore()
      const navigationMenuStore = useNavigationMenuStore()
      const restaurantStore = useRestaurantStore()
      const relishStore = useRelishStore()

      if (this.intervalId) {
        clearInterval(this.intervalId)
      }

      // Clear fetching flags for all stores to ensure that the next fetch is not blocked
      menuStore.fetching.menu = false
      promotionsStore.fetching.promotions = false
      navigationMenuStore.fetching.items = false
      orderHistoryStore.fetching.pastOrders = false
      orderHistoryStore.lastUpdated.orderStatuses = undefined
      orderHistoryStore.lastFailed.orderStatuses = undefined
      relishStore.fetching = {
        coupons: false,
        productRecommendations: false,
        eightAndDonate: false,
        tags: false,
        madBundayEntries: false
      }

      const shouldDoFetch = (
        lastUpdated: number | undefined,
        lastFailed: number | undefined,
        successfulPollingInterval: number,
        failedPollingInterval: number
      ) => {
        // First check the interval since last successful fetch
        if (!lastUpdated || Date.now() - lastUpdated > successfulPollingInterval) {
          if (!lastFailed || Date.now() - lastFailed > failedPollingInterval) {
            return true
          }
        }
        return false
      }

      let isIntervalCallbackRunning = false

      const intervalCallback = async () => {
        if (isIntervalCallbackRunning) {
          return
        }

        const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false
        const apiCircuitState = getApiCircuitBreakerState()

        if (isOffline || apiCircuitState.isOpen) {
          // If the user is offline or the API circuit breaker is open,
          // skip the polling to avoid unnecessary errors and retries
          return
        }

        isIntervalCallbackRunning = true
        const requests: Array<() => Promise<void>> = []

        try {
          // Restaurant availability (i.e. restaurant)
          if (
            shouldDoFetch(
              restaurantStore.lastUpdated.status,
              restaurantStore.lastFailed.status,
              POLLING_INTERVAL,
              POLLING_INTERVAL
            )
          ) {
            if (restaurantStore.isNonDemoRestaurantSelected && !restaurantStore.isCateringMode) {
              requests.push(restaurantStore.checkRestaurantAvailability)
            }
          }

          // Menu
          if (
            shouldDoFetch(
              menuStore.lastUpdated.menu,
              menuStore.lastFailed.menu,
              RELISH_POLLING_INTERVAL,
              RELISH_FAILED_POLLING_INTERVAL
            )
          ) {
            // Only poll for menu if a restaurant is selected
            if (restaurantStore.getSelectedRestaurant !== undefined) {
              requests.push(menuStore.fetchMenu)
            }
          }

          // Order Status
          if (
            orderHistoryStore.liveOrders.length &&
            shouldDoFetch(
              orderHistoryStore.lastUpdated.orderStatuses,
              orderHistoryStore.lastFailed.orderStatuses,
              ORDER_STATUS_INTERVAL,
              ORDER_STATUS_INTERVAL
            )
          ) {
            requests.push(orderHistoryStore.fetchOrderStatuses)

            // Cleanup order statuses if there are no active orders
          } else if (orderHistoryStore.orderStatuses.length && !orderHistoryStore.liveOrders.length) {
            orderHistoryStore.orderStatuses = []
          }

          // Promotions
          if (
            shouldDoFetch(
              promotionsStore.lastUpdated.promotions,
              promotionsStore.lastFailed.promotions,
              RELISH_POLLING_INTERVAL,
              RELISH_FAILED_POLLING_INTERVAL
            )
          ) {
            if (!restaurantStore.isCateringMode) {
              requests.push(promotionsStore.fetchPromotions)
            }
          }

          // User Menu
          if (
            shouldDoFetch(
              navigationMenuStore.lastUpdated.items,
              navigationMenuStore.lastFailed.items,
              RELISH_POLLING_INTERVAL,
              RELISH_FAILED_POLLING_INTERVAL
            )
          ) {
            // Fetch navigation menu if not in catering mode
            if (!restaurantStore.isCateringMode) {
              requests.push(navigationMenuStore.fetchNavigationMenu)
            }
          }

          if (authStore.isAuthenticated && !restaurantStore.isCateringMode) {
            const fetchRelishData = useFetchRelish()
            const relishRequests: RelishRequest[] = []

            // Relish - Product recommendations
            if (restaurantStore.isNonDemoRestaurantSelected) {
              if (
                shouldDoFetch(
                  relishStore.lastUpdated.productRecommendations,
                  relishStore.lastFailed.productRecommendations,
                  RELISH_POLLING_INTERVAL,
                  RELISH_FAILED_POLLING_INTERVAL
                )
              ) {
                relishRequests.push('productRecommendations')
              }
            }

            // Relish - Coupons
            if (
              shouldDoFetch(
                relishStore.lastUpdated.coupons,
                relishStore.lastFailed.coupons,
                RELISH_POLLING_INTERVAL,
                RELISH_FAILED_POLLING_INTERVAL
              )
            ) {
              relishRequests.push('coupons')
            }

            // Relish - Eight and Donate
            if (
              shouldDoFetch(
                relishStore.lastUpdated.eightAndDonate,
                relishStore.lastFailed.eightAndDonate,
                RELISH_POLLING_INTERVAL,
                RELISH_FAILED_POLLING_INTERVAL
              )
            ) {
              relishRequests.push('eightAndDonate')
            }

            // Relish - Order History
            if (
              shouldDoFetch(
                orderHistoryStore.lastUpdated.pastOrders,
                orderHistoryStore.lastFailed.pastOrders,
                RELISH_POLLING_INTERVAL,
                RELISH_FAILED_POLLING_INTERVAL
              )
            ) {
              relishRequests.push('orderHistory')
            }

            if (relishRequests.length) {
              requests.push(() => fetchRelishData(relishRequests))
            }
          }

          if (!requests.length) return

          await Promise.allSettled(requests.map((request) => request()))
        } finally {
          isIntervalCallbackRunning = false
        }
      }

      // Do not call the interval callback immediately unless requested
      if (callImmediately) {
        await intervalCallback()
      }

      this.intervalId = window.setInterval(intervalCallback, CALLBACK_INTERVAL)
    },
    /**
     * @description Clear the polling interval
     */
    clearPolling() {
      if (this.intervalId) {
        window.clearInterval(this.intervalId)
        this.intervalId = undefined
      }
    }
  }
})

export default usePollingStore
export { initialState }
